import React, { useState, useRef, useEffect } from 'react';
import { Phone, Search, ChevronDown } from 'lucide-react';
import { CountryCode } from '../utils/countryCodes';

interface SearchableCountrySelectProps {
  countries: CountryCode[];
  selectedCountry: CountryCode | null;
  onCountryChange: (country: CountryCode) => void;
  disabled?: boolean;
  className?: string;
  focusRingColor?: 'purple' | 'primary';
}

const SearchableCountrySelect: React.FC<SearchableCountrySelectProps> = ({
  countries,
  selectedCountry,
  onCountryChange,
  disabled = false,
  className = '',
  focusRingColor = 'purple'
}) => {
  const isPrimary = focusRingColor === 'primary';
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCountries, setFilteredCountries] = useState(countries);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter countries based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredCountries(countries);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = countries.filter(
        country =>
          country.name.toLowerCase().includes(query) ||
          country.code.toLowerCase().includes(query) ||
          country.dialCode.includes(query)
      );
      setFilteredCountries(filtered);
    }
  }, [searchQuery, countries]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Focus search input when dropdown opens
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (country: CountryCode) => {
    onCountryChange(country);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full flex items-center justify-between gap-2 px-3 py-3 border border-gray-300 rounded-lg
          ${isPrimary ? 'focus:ring-2 focus:ring-primary-500' : 'focus:ring-2 focus:ring-purple-500'} focus:border-transparent
          bg-white text-left
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-gray-400'}
          ${isOpen ? (isPrimary ? 'ring-2 ring-primary-500 border-primary-500' : 'ring-2 ring-purple-500 border-purple-500') : ''}
        `}
        style={{ minWidth: '120px', maxWidth: '180px' }}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
          {selectedCountry ? (
            <>
              <img
                src={selectedCountry.flagPath}
                alt={selectedCountry.name}
                className="w-5 h-5 object-cover rounded-sm flex-shrink-0"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <span className="text-sm font-medium truncate">{selectedCountry.dialCode}</span>
            </>
          ) : (
            <span className="text-sm text-gray-500">Select</span>
          )}
        </div>
        <ChevronDown
          className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${
            isOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-hidden"
          style={{ minWidth: '320px', width: 'max-content', maxWidth: '400px' }}>
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search country..."
                  className={`w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md ${isPrimary ? 'focus:ring-2 focus:ring-primary-500' : 'focus:ring-2 focus:ring-purple-500'} focus:border-transparent`}
              />
            </div>
          </div>

          {/* Country List */}
          <div className="overflow-y-auto max-h-64">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => handleSelect(country)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-100
                    transition-colors
                    ${selectedCountry?.code === country.code ? (focusRingColor === 'primary' ? 'bg-primary-50' : 'bg-purple-50') : ''}
                  `}
                >
                  <img
                    src={country.flagPath}
                    alt={country.name}
                    className="w-5 h-5 object-cover rounded-sm flex-shrink-0"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 whitespace-nowrap">
                      {country.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {country.dialCode} • {country.code}
                    </div>
                  </div>
                  {selectedCountry?.code === country.code && (
                    <span className={`text-${focusRingColor === 'primary' ? 'primary' : 'purple'}-600 text-sm flex-shrink-0`}>✓</span>
                  )}
                </button>
              ))
            ) : (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                No countries found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableCountrySelect;

